import type { Ref } from 'vue';
import { isOverlayWheelTarget } from '../../lib/containOverlayWheel';

/**
 * Wheel handling for wide / nested grids:
 * 1) Shift+wheel and clearly dominant trackpad deltaX scroll `.table`
 *    horizontally when it overflows — browsers do NOT reliably map
 *    shift+wheel onto overflow:auto divs, and focused INPUT cells often
 *    swallow deltaX. Slightly diagonal vertical gestures must NOT steal
 *    the page scroll (absX > absY, not >=).
 * 2) Optional `wheelRequiresFocus`: while focus is OUTSIDE the table,
 *    vertical wheel over the grid scrolls the surrounding page — hovering
 *    is not enough. The grid takes vertical wheel only after a real
 *    interaction (click / scrollbar). Once focused, the grid keeps wheel
 *    ownership at its vertical edges until the user leaves it. Horizontal
 *    intent (shift+wheel / dominant deltaX) still scrolls a wide grid.
 */

export function nearestVerticalScroller(from: HTMLElement | null): HTMLElement | null {
  for (let p = from; p; p = p.parentElement) {
    const style = getComputedStyle(p);
    if (/(auto|scroll)/.test(style.overflowY) && p.scrollHeight > p.clientHeight) {
      return p;
    }
  }
  return (document.scrollingElement as HTMLElement | null) ?? null;
}

/**
 * Wheel ownership latch. Virtualization unmounts the focused editor cell while
 * the user is still turning the wheel; the resulting focusout flips
 * `tableFocused` to false between two ticks and the very next tick would be
 * sent to the page — the grid scrolls a bit, then the whole page flies away.
 * A grid that handled a vertical wheel keeps ownership for `WHEEL_LATCH_MS`
 * after the last tick; an explicit release (click outside) clears it.
 */
export interface WheelLatch {
  ownedUntil: number;
}

export const WHEEL_LATCH_MS = 1500;

export interface TableWheelOptions {
  tableRef: Ref<HTMLElement | undefined>;
  wrapperRef: Ref<HTMLElement | undefined>;
  tableFocused: Ref<boolean>;
  wheelRequiresFocus: () => boolean;
  /** User is scrolling the grid itself (not chaining to the page). */
  onUserInteract?: () => void;
  /** Shared ownership latch, see {@link WheelLatch}. */
  wheelLatch?: WheelLatch;
  /** Clock for the latch — injectable for tests. */
  now?: () => number;
}

function isFromNestedDialog(event: WheelEvent): boolean {
  const dialog = event.target instanceof Element ? event.target.closest('dialog') : null;
  const grid = event.currentTarget;
  return !!dialog && grid instanceof Node && grid.contains(dialog);
}

/** Pure handler — exported for unit tests without mounting NxTable. */
export function handleTableWheel(event: WheelEvent, opts: TableWheelOptions): void {
  // Typ-menu / select / filter popovers sit over the grid — never steal
  // their wheel for the table or the page.
  if (isOverlayWheelTarget(event.target)) return;
  // A cell's own dialog (the full editor of a text cell) sits in the grid's
  // DOM, so its wheel bubbles here — it scrolls the dialog, never the grid or
  // the page behind it (BLP/nx#2861). A grid that sits in a dialog itself is
  // not affected: that dialog is no descendant of the grid.
  if (isFromNestedDialog(event)) return;

  const table = opts.tableRef.value;
  if (!table) return;

  const absX = Math.abs(event.deltaX);
  const absY = Math.abs(event.deltaY);
  const canScrollX = table.scrollWidth > table.clientWidth + 1;

  const horizontalIntent = event.shiftKey || (canScrollX && absX > absY);
  if (horizontalIntent && canScrollX) {
    const dx = event.shiftKey ? event.deltaY || event.deltaX : event.deltaX;
    const atStart = table.scrollLeft <= 0;
    const atEnd = table.scrollLeft + table.clientWidth >= table.scrollWidth - 1;
    const canMove = (dx < 0 && !atStart) || (dx > 0 && !atEnd);
    if (canMove) {
      event.preventDefault();
      table.scrollBy({ left: dx });
      opts.onUserInteract?.();
      return;
    }
  }

  if (absY <= absX && !event.shiftKey) return;

  const atTop = table.scrollTop <= 0;
  const atBottom = table.scrollTop + table.clientHeight >= table.scrollHeight - 1;
  const goingUp = event.deltaY < 0;
  const goingDown = event.deltaY > 0;
  const tableCanScrollY = (goingUp && !atTop) || (goingDown && !atBottom);

  const requiresFocus = opts.wheelRequiresFocus();
  const now = opts.now ?? (() => performance.now());
  const latched = !!opts.wheelLatch && opts.wheelLatch.ownedUntil > now();
  const focused = opts.tableFocused.value || latched;
  if (requiresFocus && focused) {
    // Focus is the ownership boundary: reaching the first/last row must not
    // suddenly move the surrounding page. The next outside click releases it.
    if (goingUp || goingDown) event.preventDefault();
    if (opts.wheelLatch) opts.wheelLatch.ownedUntil = now() + WHEEL_LATCH_MS;
    if (tableCanScrollY) {
      // Drive the scroll ourselves so virtualization can restore table
      // ownership after it unmounts the previously focused row.
      table.scrollBy({ top: event.deltaY });
      opts.onUserInteract?.();
    }
    return;
  }

  const sendToPage = requiresFocus && !focused;

  if (!sendToPage) return;

  const host = nearestVerticalScroller(opts.wrapperRef.value?.parentElement ?? null);
  if (!host || host === table) return;
  event.preventDefault();
  host.scrollBy({ top: event.deltaY });
}

export function useTableWheel(opts: TableWheelOptions) {
  const wheelLatch: WheelLatch = opts.wheelLatch ?? { ownedUntil: 0 };
  const merged: TableWheelOptions = { ...opts, wheelLatch };
  return {
    onTableWheel: (event: WheelEvent) => handleTableWheel(event, merged),
    /** Drop wheel ownership — the user left the grid for real (click outside). */
    releaseWheelLatch: () => {
      wheelLatch.ownedUntil = 0;
    },
  };
}
